// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState, useContext, useEffect, SyntheticEvent } from 'react';
import copy from 'copy-to-clipboard';
import { useRouter } from 'next/router';

import { apiPath, webPath, eventTypePath } from 'presentations/routes/path';
import {
  DashboardPortalContext,
  setInfoMessage,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { post, patch } from 'infras/rest_client';
import { EventTypeListItem } from 'presentations/api/schema/event_type';
import { ViewerItem } from 'presentations/api/schema/user';
import { PendingEventItem } from 'presentations/api/schema/pending_event';
import * as tracker from 'presentations/views/helpers/analytics';

interface Props {
  viewer: ViewerItem;
  eventType: EventTypeListItem;
  onClose?: any;
}

export function useOnetimeLinkDialog(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const { dispatch: navigationDispatch } = useContext(DashboardPortalContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pendingEvent, setPendingEvent] = useState<PendingEventItem | null>(
    null,
  );
  const [onetimeLinkUrl, setOnetimeLinkUrl] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(props.eventType.name);

  useEffect(() => {
    (async () => {
      await createOnetimeLink();
    })();
  }, []);

  const createOnetimeLink = async () => {
    const result = await post(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/event_types/${
          props.eventType.uid
        }/create_onetime_link`,
      ),
    );
    if (!result.isSuccess) {
      navigationDispatch(setErrorMessage(t('Failed create onetime link')));
      props.onClose();
    }
    const pendingEvent: PendingEventItem = result.data;

    tracker.setEvent('onetime link create', {
      teamId: props.viewer.team!.id,
      planId: props.viewer.planId,
      pendEventId: pendingEvent.id,
      eventTypeId: pendingEvent.eventTypeId,
      name: pendingEvent.name,
    });

    setOnetimeLinkUrl(
      webPath(
        eventTypePath({
          teamSlug: props.viewer.team!.slug,
          eventTypeSlug: props.eventType.slug,
          onetimeLinkId: pendingEvent.uid,
        }),
        router.locale,
      ),
    );
    setPendingEvent(pendingEvent);
  };

  const onChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    switch (name) {
      case 'name':
        setName(value);
        break;
      default:
        break;
    }
  };

  const onSubmit = async () => {
    if (isLoading) return;
    if (!name || !onetimeLinkUrl || !pendingEvent) return;

    setIsLoading(true);
    // ワンタイムリンク名更新
    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/pending_events/${
          pendingEvent.uid
        }/update`,
        {
          name,
        },
      ),
    );
    if (!result.isSuccess) {
      setIsLoading(false);
      navigationDispatch(setErrorMessage(t('Failed update onetime link')));
      return;
    }

    tracker.setEvent('onetime link update', {
      teamId: props.viewer.team!.id,
      planId: props.viewer.planId,
      pendEventId: pendingEvent.id,
      eventTypeId: pendingEvent.eventTypeId,
      name: pendingEvent.name,
    });

    setIsLoading(false);
    copy(onetimeLinkUrl, undefined);
    navigationDispatch(setInfoMessage(t('Copied')));
    props.onClose();
  };

  return {
    state: {
      name,
      onetimeLinkUrl,
      isLoading,
    },
    actions: {
      onChange,
      onSubmit,
    },
  };
}
